import { 
  ChirpsFilesResponse,
  ChirpsFilesError,
  ChirpsFile,
  FilesSortConfig,
  ChirpsDataRequestParams,
  ChirpsDataRequestResponse,
  ChirpsDataRequestError
} from '../../../types/files';
import { ClimateApiError } from '../base/ClimateApiError';
import { ClimateApiErrorType } from '../base/types';

/**
 * Files Service
 * 
 * Handles CHIRPS files API operations including listing and data transformation.
 */
export class FilesService {
  private baseUrl: string;

  constructor(baseUrl: string = '/api/climate') {
    this.baseUrl = baseUrl;
  }

  /**
   * Get CHIRPS files list
   */
  async getFiles(): Promise<ChirpsFilesResponse> {
    try {
      const url = `${this.baseUrl}/data/files`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json() as ChirpsFilesError;
        throw new ClimateApiError(
          ClimateApiErrorType.SERVER_ERROR,
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData
        );
      }

      const data = await response.json() as ChirpsFilesResponse;
      
      if (!data.success) {
        const errorResponse = data as unknown as ChirpsFilesError;
        throw new ClimateApiError(
          ClimateApiErrorType.SERVER_ERROR,
          errorResponse.error || 'API returned success: false',
          response.status,
          data
        );
      }

      return data;
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      
      throw new ClimateApiError(
        ClimateApiErrorType.NETWORK_ERROR,
        'Failed to fetch CHIRPS files',
        0,
        error
      );
    }
  }

  /**
   * Request CHIRPS data download
   */
  async requestChirpsData(params: ChirpsDataRequestParams): Promise<ChirpsDataRequestResponse> {
    try {
      // Build query parameters from the request data
      const queryParams = new URLSearchParams();
      queryParams.append('temporal_resolution', params.temporal_resolution);
      queryParams.append('region', params.region);
      queryParams.append('data_source', params.data_source);
      queryParams.append('quality', params.quality);
      queryParams.append('format', params.format);
      queryParams.append('years', params.years);
      
      const url = `${this.baseUrl}/data/request?${queryParams.toString()}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        }
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json() as unknown;
          // Try to extract error message from various possible structures
          if (errorData && typeof errorData === 'object') {
            const data = errorData as Record<string, unknown>;
            if (data.error && typeof data.error === 'object') {
              const error = data.error as Record<string, unknown>;
              if (typeof error.message === 'string') {
                errorMessage = error.message;
              } else if (typeof error.details === 'string') {
                errorMessage = error.details;
              }
            } else if (typeof data.error === 'string') {
              errorMessage = data.error;
            } else if (typeof data.message === 'string') {
              errorMessage = data.message;
            }
          }
        } catch (parseError) {
          // If JSON parsing fails, use the default error message
          console.warn('Failed to parse error response:', parseError);
        }
        
        throw new ClimateApiError(
          ClimateApiErrorType.SERVER_ERROR,
          errorMessage,
          response.status,
          response
        );
      }

      const data = await response.json() as ChirpsDataRequestResponse;
      
      if (!data.success) {
        const errorResponse = data as unknown as ChirpsDataRequestError;
        throw new ClimateApiError(
          ClimateApiErrorType.SERVER_ERROR,
          errorResponse.error || 'API returned success: false',
          response.status,
          data
        );
      }

      return data;
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      
      throw new ClimateApiError(
        ClimateApiErrorType.NETWORK_ERROR,
        'Failed to request CHIRPS data',
        0,
        error
      );
    }
  }

  /**
   * Transform raw file data for display
   */
  transformFileForDisplay(file: ChirpsFile) {
    return {
      ...file,
      // Format sizes for display
      total_size_display: `${file.total_size_mb.toFixed(2)} MB`,
      average_size_display: `${file.average_size_mb.toFixed(2)} MB`,
      
      // Format dates for display
      oldest_file_display: new Date(file.oldest_file_date).toLocaleDateString(),
      newest_file_display: new Date(file.newest_file_date).toLocaleDateString(),
      
      // Format metadata for display
      metadata_display: Object.entries(file.metadata)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')
    };
  }

  /**
   * Sort files array
   * Client-side sorting since no server pagination needed
   */
  sortFiles(files: ChirpsFile[], sortConfig: FilesSortConfig): ChirpsFile[] {
    return [...files].sort((a, b) => {
      let comparison = 0;
      
      switch (sortConfig.field) {
        case 'folder_path':
          comparison = a.folder_path.localeCompare(b.folder_path);
          break;
        case 'file_count':
          comparison = a.file_count - b.file_count;
          break;
        case 'total_size_mb':
          comparison = a.total_size_mb - b.total_size_mb;
          break;
        case 'oldest_file_date':
          comparison = new Date(a.oldest_file_date).getTime() - new Date(b.oldest_file_date).getTime();
          break;
        case 'newest_file_date':
          comparison = new Date(a.newest_file_date).getTime() - new Date(b.newest_file_date).getTime();
          break;
        default:
          return 0;
      }
      
      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });
  }

  /**
   * Get total statistics for all files
   * Summary statistics helper method
   */
  getFileStatistics(files: ChirpsFile[]) {
    return {
      total_folders: files.length,
      total_files: files.reduce((sum, file) => sum + file.file_count, 0),
      total_size_mb: files.reduce((sum, file) => sum + file.total_size_mb, 0),
      average_files_per_folder: files.length > 0 
        ? files.reduce((sum, file) => sum + file.file_count, 0) / files.length 
        : 0
    };
  }

  /**
   * Trigger CHIRPS chunk generation for Zimbabwe
   * Returns job ID for tracking in Job Management
   */
  async triggerChirpsChunkGeneration(): Promise<{
    job_id: string;
    job_type: string;
    status: string;
    region_name: string;
    expected_chunks: number;
    estimated_processing_time_minutes: number;
    message: string;
  }> {
    try {
      const ZIMBABWE_PARAMS = {
        lat_min: -23.0,
        lat_max: -15.0,
        lng_min: 25.0,
        lng_max: 34.0,
        chunk_size: 1.0,
        region_name: "zimbabwe"
      };

      const url = `${this.baseUrl}/spatial/chirps-chunks`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token') || ''}`
        },
        body: JSON.stringify(ZIMBABWE_PARAMS)
      });

      if (!response.ok) {
        const errorData = await response.json() as { success: boolean; error?: string };
        throw new ClimateApiError(
          ClimateApiErrorType.SERVER_ERROR,
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData
        );
      }

      const data = await response.json() as { 
        success: boolean; 
        job_id: string;
        job_type: string;
        status: string;
        region_name: string;
        expected_chunks: number;
        estimated_processing_time_minutes: number;
        message: string;
        error?: string;
      };
      
      if (!data.success) {
        throw new ClimateApiError(
          ClimateApiErrorType.SERVER_ERROR,
          data.error || 'API returned success: false',
          response.status,
          data
        );
      }

      return {
        job_id: data.job_id,
        job_type: data.job_type,
        status: data.status,
        region_name: data.region_name,
        expected_chunks: data.expected_chunks,
        estimated_processing_time_minutes: data.estimated_processing_time_minutes,
        message: data.message
      };
    } catch (error) {
      if (error instanceof ClimateApiError) {
        throw error;
      }
      
      throw new ClimateApiError(
        ClimateApiErrorType.NETWORK_ERROR,
        'Failed to trigger CHIRPS chunk generation',
        0,
        error
      );
    }
  }
} 